import React, { PureComponent } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Typography,
	Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PageDataContext } from '../../../core/PageDataContext';
import { withRouter } from '../../../hooks/withRouter';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StraightenIcon from '@mui/icons-material/Straighten';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TimerIcon from '@mui/icons-material/Timer';
import PaletteIcon from '@mui/icons-material/Palette';

class ManualPage extends PureComponent {
	componentDidMount() {
		this.context.setPageData({ title: 'App Manual' });
	}

	renderSectionHeader = (icon, title, subtitle) => (
		<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, mt: 1 }}>
			<Box sx={{
				width: 36, height: 36, borderRadius: '50%', flexShrink: 0, mt: 0.25,
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
			}}>
				{React.cloneElement(icon, { sx: { fontSize: 20, color: 'text.secondary' } })}
			</Box>
			<Box>
				<Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}>
					{title}
				</Typography>
				{subtitle && (
					<Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
						{subtitle}
					</Typography>
				)}
			</Box>
		</Box>
	)

	renderAccordion = (title, children) => (
		<Accordion
			disableGutters
			sx={{
				bgcolor: 'background.paper',
				boxShadow: 'none',
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: '10px !important',
				'&:before': { display: 'none' },
				mb: 1.5,
				overflow: 'hidden',
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
				<Typography variant="body2" sx={{ fontWeight: 600 }}>
					{title}
				</Typography>
			</AccordionSummary>
			<AccordionDetails sx={{ pt: 0 }}>
				{children}
			</AccordionDetails>
		</Accordion>
	)

	renderBodyText = (text) => (
		<Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}>
			{text}
		</Typography>
	)

	renderList = (items) => (
		<Box component="ul" sx={{ pl: 2.5, my: 1, '& li': { mb: 0.5 } }}>
			{items.map((item, i) => (
				<li key={i}>
					<Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
						{item}
					</Typography>
				</li>
			))}
		</Box>
	)

	renderStep = (number, text) => (
		<Box sx={{ display: 'flex', gap: 1.5, mb: 1.25 }}>
			<Box sx={{
				width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
				bgcolor: (theme) => `${theme.palette.secondary.main}18`,
				display: 'flex', alignItems: 'center', justifyContent: 'center',
			}}>
				<Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12, color: 'secondary.main' }}>
					{number}
				</Typography>
			</Box>
			<Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, pt: 0.15 }}>
				{text}
			</Typography>
		</Box>
	)

	render() {
		return (
			<Box sx={{ pt: 2, pb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>

				{/* Quick Start */}
				<Box>
					{this.renderSectionHeader(
						<PlayArrowIcon />,
						'Quick Start',
						'Get up and running in under a minute.'
					)}

					<Box sx={{
						bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
						borderRadius: 2.5, p: 2.5,
					}}>
						{this.renderStep(1, <><strong>Create a routine</strong> — go to the Routines tab, tap the menu icon, and select "New Routine". Give it a name (e.g. "Push Day").</>)}
						{this.renderStep(2, <><strong>Add exercises</strong> — open your new routine, tap the menu icon, then "Add Exercise". Pick from 100+ pre-loaded exercises or create your own.</>)}
						{this.renderStep(3, <><strong>Start a workout</strong> — tap the play button on the Home page, pick a routine, and start logging sets. The app tracks your workout time automatically.</>)}
						{this.renderStep(4, <><strong>Finish your workout</strong> — when you're done, tap the active workout indicator and hit "Complete Workout". You'll see your stats, any new personal records, and earned badges.</>)}
					</Box>
				</Box>

				{/* Routines & Exercises */}
				<Box>
					{this.renderSectionHeader(
						<FitnessCenterIcon />,
						'Routines & Exercises',
						'How your training is organized.'
					)}

					{this.renderBodyText(
						'Routines are your workout templates — like "Upper Body", "Leg Day", or "Full Body A". Each routine contains exercises. Exercises exist independently and can appear in multiple routines, so your log history follows the exercise, not the routine.'
					)}

					{this.renderAccordion('Creating exercises', <>
						{this.renderBodyText('You can add exercises in two ways:')}
						{this.renderList([
							<><strong>From a Routine</strong> (easiest) — open a routine, tap the menu icon, select "Add Exercise". You'll see all your exercises plus 100+ pre-loaded ones. Tap one to add it, or create a custom exercise with the + icon.</>,
							<><strong>From the Exercises tab</strong> — tap the + button to create a standalone exercise. You can link it to routines later.</>,
						])}
						{this.renderBodyText('When creating a custom exercise, you choose a category, type (e.g. Weight & Reps, Reps Only, Distance & Time), and optionally a muscle breakdown. The muscle breakdown powers the muscle chart on your Workout Detail page.')}
					</>)}

					{this.renderAccordion('Exercise types explained', <>
						{this.renderList([
							<><strong>Weight & Reps</strong> — for most strength exercises (bench press, squats, rows).</>,
							<><strong>Reps</strong> — for bodyweight exercises (pull-ups, push-ups).</>,
							<><strong>Distance & Time</strong> — for cardio (running, cycling, rowing).</>,
							<><strong>Time</strong> — for holds or timed exercises (planks, wall sits).</>,
							<><strong>Weight & Time</strong> — for weighted holds (farmer's walks, dead hangs with weight).</>,
						])}
					</>)}

					{this.renderAccordion('Reordering, archiving & supersets', <>
						{this.renderBodyText('Drag the reorder icon on the bottom-right of exercise cards to rearrange them. Use the three-dot menu on any exercise card to archive it (hides it from workouts but keeps your history) or add it to a superset.')}
						{this.renderBodyText('Supersets link exercises together so logging a set automatically switches you to the next exercise in the group — or after the rest timer ends. Create a superset from the exercise card menu, then add other exercises to the same superset.')}
					</>)}
				</Box>

				{/* Logging & Workouts */}
				<Box>
					{this.renderSectionHeader(
						<PlayArrowIcon />,
						'Logging & Workouts',
						'Recording your training sessions.'
					)}

					{this.renderBodyText(
						'Tap an exercise inside a routine to open the logger. The logger has three tabs: Plan (preset your sets), Log (record live sets), and your workout history table. You can add RPE, RIR, and comments to each set.'
					)}

					{this.renderAccordion('Starting a workout', <>
						{this.renderBodyText('There are several ways to start:')}
						{this.renderList([
							<><strong>Home page</strong> — tap the play button, pick a routine. This is the fastest way.</>,
							<><strong>Routine page</strong> — tap "Start Workout" in the action bar at the top.</>,
							<><strong>Just start logging</strong> — open any exercise and save a set. A workout starts automatically.</>,
							<><strong>Past workouts</strong> — use the three-dot menu in a routine and select "Start Past Workout" to log a session from a previous date.</>,
						])}
					</>)}

					{this.renderAccordion('During a workout', <>
						{this.renderBodyText('While a workout is active, you\'ll see two indicators:')}
						{this.renderList([
							<>A pulsing "WORKING" badge in the top navigation bar — tap it to see your current workout.</>,
							<>A colored action button in the exercise logger — green means no active workout, red means one is running.</>,
						])}
						{this.renderBodyText('The rest timer starts automatically when you save a set with the timer icon. It supports audio alerts, vibration, and push notifications so you can leave the app while resting.')}
					</>)}

					{this.renderAccordion('Completing a workout', <>
						{this.renderBodyText('When you\'re done, open the Workout Detail page and tap "Complete Workout". You can also edit the start/end time and rate your mood (good, okay, bad) — this helps the AI give better insights.')}
						{this.renderBodyText('After completing, you\'ll see your workout summary: total volume, duration, exercises, and any personal records you hit. If you earned new achievement badges, those appear too.')}
					</>)}

					{this.renderAccordion('Forgot to end your workout?', <>
						{this.renderBodyText('No problem. Open the Workout Detail page and use the edit button to adjust the end time manually. The duration will update accordingly.')}
					</>)}
				</Box>

				{/* Goals */}
				<Box>
					{this.renderSectionHeader(
						<TrackChangesIcon />,
						'Goals',
						'Set targets and track your progress.'
					)}

					{this.renderBodyText(
						'You can create goals for any exercise — weight targets, rep targets, time targets, or combinations. Goals are visible in the exercise logger (Goals tab), and also on the Home page under the Goals card.'
					)}

					{this.renderAccordion('Goal types & options', <>
						{this.renderBodyText('The available goal types depend on the exercise type. For example, a Weight & Reps exercise lets you set weight goals, rep goals, or combined weight+reps goals.')}
						{this.renderList([
							<><strong>Deadline</strong> — optionally set a target date.</>,
							<><strong>Routine-specific</strong> — track progress within one routine only, or across all routines that use the exercise.</>,
							<><strong>Up to 3 active goals</strong> per exercise at a time.</>,
						])}
					</>)}
				</Box>

				{/* Stats & Progress */}
				<Box>
					{this.renderSectionHeader(
						<BarChartIcon />,
						'Stats & Progress',
						'Understand your training data.'
					)}

					{this.renderBodyText(
						'Every exercise has a Stats tab and a Graphs tab. Stats shows totals, averages, and personal bests. Graphs visualizes your progress over time with charts for weight, reps, volume, and estimated 1RM (Brzycki formula).'
					)}

					{this.renderAccordion('Home page stats', <>
						{this.renderList([
							<><strong>Last 7 Days</strong> — a quick glance at recent workout volume by day.</>,
							<><strong>Workout Statistics</strong> — total workouts (all time, this year, this month), workout frequency, and average duration.</>,
							<><strong>Goals</strong> — all your active goals in one place.</>,
							<><strong>Workout History</strong> — a scrollable list of all past workouts. Use the calendar icon to jump to a specific date.</>,
						])}
					</>)}

					{this.renderAccordion('Workout Detail page', <>
						{this.renderBodyText('Each workout has a detail page showing exercises performed, total volume, duration, and all logged sets. Two tabs at the bottom:')}
						{this.renderList([
							<><strong>Volume</strong> — a graph of your workout volume over time, filterable by date range.</>,
							<><strong>Muscles</strong> — a breakdown of muscles trained, based on each exercise's muscle percentage data.</>,
						])}
						{this.renderBodyText('You can also compare a workout against a previous session to see how your performance changed.')}
					</>)}
				</Box>

				{/* Streaks & Badges */}
				<Box>
					{this.renderSectionHeader(
						<EmojiEventsIcon />,
						'Streaks & Badges',
						'Stay consistent and earn achievements.'
					)}

					{this.renderBodyText(
						'The app tracks weekly workout streaks based on your weekly goal (default: 3 workouts per week, adjustable in Settings). Hit your goal each week to keep your streak alive.'
					)}
					{this.renderBodyText(
						'There are 71 achievement badges across 9 categories — earned by hitting milestones like total workouts, volume lifted, streak length, and more. Badges you earn are shown in the Home page and on workout completion.'
					)}
				</Box>

				{/* Measurements */}
				<Box>
					{this.renderSectionHeader(
						<StraightenIcon />,
						'Body Measurements',
						'Track metrics beyond the gym.'
					)}

					{this.renderBodyText(
						'The Measurements tab lets you track anything — weight, body fat, arm circumference, or any custom metric. Each measurement gets its own log history and line graph so you can see trends over time.'
					)}
					{this.renderBodyText(
						'You can color-code, reorder, and soft-delete measurements. Deleted measurements can be restored.'
					)}
				</Box>

				{/* AI */}
				<Box>
					{this.renderSectionHeader(
						<AutoAwesomeIcon />,
						'AI Features',
						'Your personal training assistant.'
					)}

					{this.renderBodyText(
						'AI features are available to everyone. Free accounts get 15 AI credits per month; Premium subscribers get 150. Each AI interaction (insight, chat message, weekly digest) uses one credit.'
					)}

					{this.renderAccordion('What the AI can do', <>
						{this.renderList([
							<><strong>Workout insights</strong> — tap the sparkle icon on any Workout Detail page to get an AI analysis of that session.</>,
							<><strong>Exercise insights</strong> — available in the exercise logger, analyzes your progress on a specific exercise.</>,
							<><strong>Chat assistant</strong> — ask follow-up questions, get advice on breaking plateaus, request new workout ideas, or just chat about your training.</>,
							<><strong>Weekly digest</strong> — a summary of your past week's training, shown on the Home page every week.</>,
							<><strong>Create routines & goals</strong> — ask the AI to build a full training program or set goals based on your data.</>,
						])}
					</>)}

					{this.renderAccordion('Where AI appears', <>
						{this.renderBodyText('Look for the sparkle icon throughout the app:')}
						{this.renderList([
							'Home page — weekly digest card and AI insights button.',
							'Workout Detail page — per-workout analysis.',
							'Exercise logger — per-exercise progress insights.',
						])}
					</>)}
				</Box>

				{/* Rest Timer */}
				<Box>
					{this.renderSectionHeader(
						<TimerIcon />,
						'Rest Timer',
						'Never miss your rest periods.'
					)}

					{this.renderBodyText(
						'The rest timer starts when you save a set using the timer icon. It floats at the bottom of the screen so you can navigate the app while it runs. It supports audio alerts, vibration, and browser push notifications.'
					)}
					{this.renderBodyText(
						'You can set custom timer durations, and choose whether the timer value applies globally (to all exercises) or per-exercise. Timer settings are accessible from the gear icon on the timer.'
					)}
				</Box>

				{/* Customization */}
				<Box>
					{this.renderSectionHeader(
						<PaletteIcon />,
						'Customization',
						'Make it yours.'
					)}

					{this.renderBodyText(
						'Visit the Customize page (from Settings > Preferences) to personalize your app. Available customizations include:'
					)}

					{this.renderList([
						<><strong>Color themes</strong> — 20 themes that change the app's entire color scheme (light and dark mode variants).</>,
						<><strong>Workout finish animations</strong> — 8 celebration effects that play when you complete a workout (confetti, fire, lightning, and more).</>,
						<><strong>PR celebration styles</strong> — 8 styles that change how personal record banners look.</>,
					])}

					{this.renderBodyText(
						'Customizations are one-time purchases through Google Play.'
					)}
				</Box>

				{/* FAQ */}
				<Box>
					{this.renderSectionHeader(
						<HelpOutlineIcon />,
						'FAQ',
						'Common questions, quick answers.'
					)}

					{this.renderAccordion('Why does the app need an internet connection?', <>
						{this.renderBodyText('The app is web-first by design. Your data is stored on secure servers so you never lose it — even if you switch phones, clear your browser, or log in from a different device. You can access it at myworkinprogress.app or through the Play Store.')}
					</>)}

					{this.renderAccordion('How do I export my data?', <>
						{this.renderBodyText('Open any exercise in a routine, tap the filter menu (bottom-left of the logger), and select "Export CSV". This downloads your complete log history for that exercise.')}
					</>)}

					{this.renderAccordion('How do I cancel my Premium subscription?', <>
						{this.renderBodyText('All subscriptions are managed through Google Play. Open the Play Store app, go to your subscriptions, and cancel from there. Your premium features remain active until the end of the billing period.')}
					</>)}

					{this.renderAccordion('Why is AI not completely free?', <>
						{this.renderBodyText('AI features have real per-request costs. Free accounts get 15 credits per month — enough for casual use. Premium subscribers get 150 credits, which covers daily use. This model lets everyone try AI features while keeping the app financially sustainable for a solo developer.')}
					</>)}

					{this.renderAccordion('Can I use the app on multiple devices?', <>
						{this.renderBodyText('Yes. Sign in with the same account (Google, Apple, or email) on any device. Your data syncs automatically since everything is stored server-side.')}
					</>)}

					{this.renderAccordion('How are personal records detected?', <>
						{this.renderBodyText('The app automatically checks for PRs when you complete a workout. It compares your logged sets against your all-time history for each exercise. New PRs are highlighted on the workout completion screen with a celebration banner.')}
					</>)}
				</Box>
			</Box>
		);
	}
}

ManualPage.contextType = PageDataContext;

export default withRouter(ManualPage);
